/**
 * AP-EVENT-01 Gate EFIN-03: Event Financial AR & AP Actuals Tab
 *
 * Extends EventFinancialsWorkspace with actual AR and AP data:
 *   - Contract revenue vs invoiced vs collected vs outstanding
 *   - Customer advances
 *   - Supplier PO commitments vs accepted vs invoiced vs paid vs outstanding
 *   - Derived margins and net cash position
 *   - Shared-cost allocations (CRUD inline)
 *   - Data quality warnings and attribution gaps
 *   - As-of-date selector
 *   - Source-document drill-down (links to AR/AP workspaces)
 *
 * Recognized revenue: NOT_YET_AVAILABLE (displayed prominently)
 *
 * Capability required: event.finance.view
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, AlertCircle, CheckCircle2, TrendingUp, TrendingDown,
  DollarSign, ShoppingCart, BarChart3, Info, Plus, ChevronRight,
  AlertTriangle, XCircle, Clock, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { eventFinanceApi, formatError } from "@/services/api";

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmt(minor, currency = "INR") {
  if (minor == null || minor === undefined) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency, minimumFractionDigits: 0,
  }).format(minor / 100);
}
function pctStr(s) {
  if (!s && s !== "0") return "—";
  const n = parseFloat(s);
  return `${n >= 0 ? "" : ""}${n.toFixed(1)}%`;
}
function today() { return new Date().toISOString().slice(0, 10); }

const MARGIN_COLOR = (pctStr) => {
  if (!pctStr || pctStr === "—") return "#6B7280";
  const n = parseFloat(pctStr);
  if (n < 0) return "#DC2626";
  if (n < 5) return "#EA580C";
  if (n < 15) return "#D97706";
  return "#166534";
};

function MetricRow({ label, value, sub, variant = "neutral", indent = false }) {
  const colors = {
    neutral: { text: "#181818" },
    green:   { text: "#166534" },
    red:     { text: "#DC2626" },
    amber:   { text: "#D97706" },
    blue:    { text: "#0176D3" },
  };
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      padding: "6px 0", borderBottom: "1px solid #F3F4F6",
      paddingLeft: indent ? 16 : 0,
    }}>
      <span style={{ fontSize: 13, color: indent ? "#6B7280" : "#374151" }}>{label}</span>
      <div style={{ textAlign: "right" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: colors[variant]?.text || "#181818" }}>
          {value}
        </span>
        {sub && <div style={{ fontSize: 10, color: "#9CA3AF" }}>{sub}</div>}
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, accent = "#0176D3" }) {
  return (
    <div style={{ background: "#FFF", borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 8 }}>
        <Icon size={15} color={accent} />
        <span style={{ fontWeight: 700, fontSize: 13, color: "#181818" }}>{title}</span>
      </div>
      <div style={{ padding: "8px 16px 12px" }}>
        {children}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function EventFinancialActualsTab({ eventId, eventName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasCap = (cap) => user?.capabilities?.includes(cap);
  const canView      = hasCap("event.finance.view");
  const canAllocate  = hasCap("event.finance.allocate");
  const canApprove   = hasCap("event.finance.allocation.approve");

  const [asOfDate,  setAsOfDate]  = useState(today());
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);

  // Allocation CRUD
  const [allocations,  setAllocations]  = useState([]);
  const [allocLoading, setAllocLoading] = useState(false);
  const [showAllocForm, setShowAllocForm] = useState(false);
  const [allocForm, setAllocForm] = useState({
    source_document_type: "SUPPLIER_INVOICE",
    source_document_id: "",
    source_document_number: "",
    source_amount_minor: "",
    allocation_percentage: "",
    reason: "",
  });
  const [savingAlloc, setSavingAlloc] = useState(false);

  const loadMetrics = useCallback(async () => {
    if (!eventId || !canView) return;
    setLoading(true);
    try {
      const res = await eventFinanceApi.getMetrics(eventId, { as_of_date: asOfDate });
      setData(res.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [eventId, asOfDate, canView]);

  const loadAllocations = useCallback(async () => {
    if (!eventId || !canView) return;
    setAllocLoading(true);
    try {
      const res = await eventFinanceApi.listAllocations(eventId, { page_size: 50 });
      setAllocations(res.data?.allocations || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setAllocLoading(false);
    }
  }, [eventId, canView]);

  useEffect(() => { loadMetrics(); loadAllocations(); }, [loadMetrics, loadAllocations]);

  const handleCreateAlloc = async () => {
    setSavingAlloc(true);
    try {
      await eventFinanceApi.createAllocation(eventId, {
        ...allocForm,
        source_amount_minor: parseInt(allocForm.source_amount_minor),
      });
      toast.success("Allocation created in DRAFT");
      setShowAllocForm(false);
      setAllocForm({ source_document_type: "SUPPLIER_INVOICE", source_document_id: "", source_document_number: "", source_amount_minor: "", allocation_percentage: "", reason: "" });
      loadAllocations();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSavingAlloc(false);
    }
  };

  const handleApproveAlloc = async (aid) => {
    try {
      await eventFinanceApi.approveAllocation(eventId, aid, { approval_notes: "Approved from Event Finance 360°" });
      toast.success("Allocation approved");
      loadAllocations(); loadMetrics();
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleReverseAlloc = async (aid) => {
    try {
      await eventFinanceApi.reverseAllocation(eventId, aid, { reversal_reason: "Reversed from Event Finance 360°" });
      toast.success("Allocation reversed");
      loadAllocations(); loadMetrics();
    } catch (err) { toast.error(formatError(err)); }
  };

  if (!canView) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <AlertCircle size={28} color="#DC2626" />
        <p style={{ color: "#6B7280", marginTop: 8, fontSize: 13 }}>event.finance.view capability required.</p>
      </div>
    );
  }

  const d    = data;
  const ar   = d?.ar_metrics || {};
  const ap   = d?.ap_metrics || {};
  const dm   = d?.derived_metrics || {};
  const bg   = d?.budget_baseline || {};
  const warn = d?.warnings || [];
  const curr = d?.currency_code || "INR";

  return (
    <div data-testid="event-finance-actuals-tab" style={{ padding: "16px 0" }}>
      {/* Controls */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 3 }}>As-of Date</label>
          <input
            data-testid="efin-as-of-date"
            type="date" value={asOfDate}
            onChange={e => setAsOfDate(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}
          />
        </div>
        <button
          data-testid="efin-refresh-btn"
          onClick={() => { loadMetrics(); loadAllocations(); }}
          disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#FFF", fontSize: 13, cursor: "pointer" }}
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        {d && (
          <div style={{ marginLeft: "auto", fontSize: 11, color: "#9CA3AF" }}>
            Computed: {d.computed_at?.slice(0, 16) || "—"} UTC
          </div>
        )}
      </div>

      {loading && <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>Loading financial metrics…</div>}

      {!loading && !d && <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Click Refresh to load.</div>}

      {!loading && d && (
        <>
          {/* Recognized Revenue Notice */}
          <div data-testid="recognized-rev-notice" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "10px 14px", marginBottom: 16, display: "flex", gap: 10 }}>
            <Info size={15} color="#D97706" style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 12, color: "#92400E" }}>
              <strong>Recognized Revenue: NOT_YET_AVAILABLE.</strong>{" "}
              {ar.recognized_revenue?.reason}
            </span>
          </div>

          {/* Warnings */}
          {warn.length > 0 && (
            <div data-testid="efin-warnings" style={{ background: "#FFF7F7", border: "1px solid #FECACA", borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#991B1B", marginBottom: 4 }}>
                Attribution Warnings ({warn.length})
              </div>
              {warn.map((w, i) => (
                <div key={i} style={{ fontSize: 11, color: "#991B1B", display: "flex", gap: 6, marginBottom: 2 }}>
                  <AlertCircle size={11} style={{ flexShrink: 0, marginTop: 1 }} />
                  {w}
                </div>
              ))}
            </div>
          )}

          {/* Margin Overview */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginBottom: 18 }}>
            {[
              { label: "Billed Margin", val: dm.operational_billed_margin_minor, pct: dm.operational_billed_margin_pct, sub: "Invoiced ex-tax – Supplier invoiced ex-tax", testid: "billed-margin-card" },
              { label: "Budget Margin", val: dm.budgeted_margin_minor, pct: dm.budgeted_margin_pct, sub: "Contract revenue – Approved budget cost", testid: "budget-margin-card" },
              { label: "Forecast Margin", val: dm.forecast_margin_minor, pct: dm.forecast_margin_pct, sub: "Invoiced ex-tax – Committed PO cost", testid: "forecast-margin-card" },
              { label: "Net Cash", val: dm.net_cash_movement_minor, pct: null, sub: "Collections + Advances – AP Payments", testid: "net-cash-card", isNet: true },
            ].map(({ label, val, pct, sub, testid, isNet }) => {
              const isNeg = (val || 0) < 0;
              const color = val == null ? "#9CA3AF" : isNeg ? "#DC2626" : "#166534";
              return (
                <div key={label} data-testid={testid} style={{ background: "#FFF", border: `1px solid #E2E8F0`, borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color }}>{val != null ? fmt(val, curr) : "—"}</div>
                  {pct && <div style={{ fontSize: 11, fontWeight: 700, color: MARGIN_COLOR(pct) }}>{pctStr(pct)}</div>}
                  {sub && <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>{sub}</div>}
                </div>
              );
            })}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            {/* AR: Revenue & Collections */}
            <SectionCard title="Revenue & Customer Collections" icon={DollarSign} accent="#0176D3">
              <MetricRow label="Contract Revenue"    value={fmt(ar.contract_revenue_minor, curr)}    variant="blue" />
              <MetricRow label="Invoiced Revenue (incl. tax)" value={fmt(ar.invoiced_revenue_minor, curr)}   sub={`${ar.invoice_count} invoice${ar.invoice_count !== 1 ? "s" : ""}`} />
              <MetricRow label="  Tax on Invoices"   value={fmt(ar.gst_on_invoices_minor, curr)}     indent />
              <MetricRow label="  Revenue (ex-tax)"  value={fmt(ar.invoiced_revenue_ex_tax_minor, curr)} indent />
              <MetricRow label="Cash Collected"      value={fmt(ar.customer_collections_minor, curr)} variant="green"
                sub={`${ar.allocation_count} allocation${ar.allocation_count !== 1 ? "s" : ""}`} />
              <MetricRow label="Outstanding AR"      value={fmt(ar.outstanding_ar_minor, curr)}     variant={ar.outstanding_ar_minor > 0 ? "red" : "green"} />
              <MetricRow label="Customer Advances"   value={fmt(ar.customer_advances_minor, curr)}  variant="amber" />
              <MetricRow label="  Applied Advances"  value={fmt(ar.advances_applied_minor, curr)}   indent />
              <MetricRow label="Credit Adjustments"  value={fmt(ar.credit_adjustments_minor, curr)} />
              <MetricRow label="Refunds Issued"      value={fmt(ar.refunds_issued_minor, curr)}     variant={ar.refunds_issued_minor > 0 ? "red" : "neutral"} />
              {/* GAP-01 Fix: Create Invoice button with event attribution pre-populated */}
              {user?.capabilities?.includes("ar.invoice.create") && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #F3F4F6" }}>
                  <button
                    data-testid="create-event-invoice-btn"
                    onClick={() => navigate(`/ar/invoices?event_master_id=${encodeURIComponent(eventId)}&event_name=${encodeURIComponent(eventName || "")}&open_create=1`)}
                    style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#0176D3", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontWeight: 600 }}
                  >
                    <Plus size={12} /> Create Customer Invoice for This Event
                  </button>
                </div>
              )}
            </SectionCard>

            {/* AP: Costs & Payments */}
            <SectionCard title="Supplier Costs & Payments" icon={ShoppingCart} accent="#DC2626">
              <MetricRow label="Approved Budget"     value={bg.available ? fmt(bg.approved_cost_minor, curr) : "—"} variant="blue" />
              <MetricRow label="PO Commitments"      value={fmt(ap.committed_cost_minor, curr)}      sub={`${ap.po_count} PO${ap.po_count !== 1 ? "s" : ""}`} />
              <MetricRow label="Accepted Costs"      value={fmt(ap.accepted_cost_minor, curr)}       variant="green" />
              <MetricRow label="Supplier Invoiced"   value={fmt(ap.supplier_invoiced_minor, curr)}   sub={`${ap.supplier_invoice_count} invoice${ap.supplier_invoice_count !== 1 ? "s" : ""}`} />
              <MetricRow label="  Tax on Supplier Inv" value={fmt(ap.gst_on_supplier_invoices_minor, curr)} indent />
              <MetricRow label="  Supplier Invoiced (ex-tax)" value={fmt(ap.supplier_invoiced_ex_tax_minor, curr)} indent />
              <MetricRow label="Cash Paid"           value={fmt(ap.supplier_payments_minor, curr)}   variant="green"
                sub={`${ap.payment_allocation_count} payment${ap.payment_allocation_count !== 1 ? "s" : ""}`} />
              <MetricRow label="Outstanding AP"      value={fmt(ap.outstanding_ap_minor, curr)}      variant={ap.outstanding_ap_minor > 0 ? "red" : "green"} />
              <MetricRow label="Supplier Credit Notes" value={fmt(ap.supplier_credit_notes_minor, curr)} />
            </SectionCard>
          </div>

          {/* Shared-Cost Allocations */}
          <div style={{ background: "#FFF", borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden", marginBottom: 14 }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={15} color="#0176D3" />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#181818" }}>Shared-Cost Allocations</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#6B7280" }}>
                {d.shared_allocations?.approved_count || 0} approved · {d.shared_allocations?.draft_pending || 0} draft pending
              </span>
              {canAllocate && (
                <button
                  data-testid="add-allocation-btn"
                  onClick={() => setShowAllocForm(true)}
                  style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: "none", background: "#0176D3", color: "#FFF", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                >
                  <Plus size={11} /> Add
                </button>
              )}
            </div>

            {/* Add allocation form */}
            {showAllocForm && (
              <div style={{ background: "#F7F9FC", padding: "14px 16px", borderBottom: "1px solid #E2E8F0" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 3 }}>Document Type</label>
                    <select value={allocForm.source_document_type} onChange={e => setAllocForm(f => ({ ...f, source_document_type: e.target.value }))}
                      style={{ width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }}>
                      {["SUPPLIER_INVOICE", "PO", "SERVICE_ACCEPTANCE", "CUSTOMER_INVOICE"].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 3 }}>Source Document ID</label>
                    <input value={allocForm.source_document_id} onChange={e => setAllocForm(f => ({ ...f, source_document_id: e.target.value }))}
                      placeholder="Document ID" style={{ width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 3 }}>Source Amount (minor)</label>
                    <input type="number" value={allocForm.source_amount_minor} onChange={e => setAllocForm(f => ({ ...f, source_amount_minor: e.target.value }))}
                      placeholder="e.g. 5000000" style={{ width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 3 }}>Allocation % (max 100)</label>
                    <input value={allocForm.allocation_percentage} onChange={e => setAllocForm(f => ({ ...f, allocation_percentage: e.target.value }))}
                      placeholder="e.g. 30.00" style={{ width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 3 }}>Reason</label>
                    <input value={allocForm.reason} onChange={e => setAllocForm(f => ({ ...f, reason: e.target.value }))}
                      placeholder="Reason for allocation" style={{ width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button data-testid="save-alloc-btn" onClick={handleCreateAlloc} disabled={savingAlloc}
                    style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#0176D3", color: "#FFF", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                    {savingAlloc ? "Saving…" : "Save Draft"}
                  </button>
                  <button onClick={() => setShowAllocForm(false)}
                    style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #E2E8F0", background: "#FFF", fontSize: 12, cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Allocations table */}
            {allocLoading ? <div style={{ padding: 16, textAlign: "center", color: "#6B7280", fontSize: 13 }}>Loading…</div> : (
              allocations.length === 0 ? (
                <div style={{ padding: "18px 16px", textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
                  No shared-cost allocations recorded for this event.
                </div>
              ) : (
                <table data-testid="allocations-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC" }}>
                      {["Doc Type", "Doc ID", "Source Amount", "Alloc %", "Allocated", "Status", "Actions"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#374151", borderBottom: "1px solid #E2E8F0" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allocations.map((a, i) => (
                      <tr key={a.id} data-testid={`alloc-row-${i}`} style={{ borderBottom: "1px solid #F3F4F6" }}>
                        <td style={{ padding: "8px 12px", color: "#374151" }}>{a.source_document_type}</td>
                        <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 11, color: "#374151" }}>{(a.source_document_number || a.source_document_id || "—").slice(0, 18)}</td>
                        <td style={{ padding: "8px 12px" }}>{fmt(a.source_amount_minor, curr)}</td>
                        <td style={{ padding: "8px 12px", fontWeight: 700 }}>{a.allocation_percentage}%</td>
                        <td style={{ padding: "8px 12px", fontWeight: 700, color: "#0176D3" }}>{fmt(a.allocated_amount_minor, curr)}</td>
                        <td style={{ padding: "8px 12px" }}>
                          <span style={{
                            padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700,
                            background: a.status === "APPROVED" ? "#F0FDF4" : a.status === "REVERSED" ? "#FEF2F2" : "#F7F9FC",
                            color: a.status === "APPROVED" ? "#166534" : a.status === "REVERSED" ? "#DC2626" : "#6B7280",
                          }}>{a.status}</span>
                        </td>
                        <td style={{ padding: "8px 12px" }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            {canApprove && a.status !== "APPROVED" && a.status !== "REVERSED" && (
                              <button data-testid={`approve-alloc-${i}`} onClick={() => handleApproveAlloc(a.id)}
                                style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid #166534", background: "#F0FDF4", color: "#166534", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>
                                Approve
                              </button>
                            )}
                            {canAllocate && a.status !== "REVERSED" && (
                              <button data-testid={`reverse-alloc-${i}`} onClick={() => handleReverseAlloc(a.id)}
                                style={{ padding: "3px 8px", borderRadius: 5, border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontSize: 10, cursor: "pointer" }}>
                                Reverse
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>

          {/* Financial definitions note */}
          <div style={{ background: "#F7F9FC", border: "1px solid #E5E5E5", borderRadius: 10, padding: "10px 14px", fontSize: 11, color: "#706E6B" }}>
            <strong>Definitions:</strong>{" "}
            {dm.note}{" "}
            Recognized revenue requires a milestone-recognition policy — NOT_YET_AVAILABLE.{" "}
            All amounts in {curr}. Tax-inclusive totals shown; ex-tax values labeled.
          </div>
        </>
      )}
    </div>
  );
}
